import { handleActions, combineActions } from 'redux-actions';

import { CAPS } from '../constants';
import { capabilityUtils } from 'moet-core';

const merchantUpdateDataActions = capabilityUtils.getFinalizedActions([
  CAPS.V2_MERCHANTS_ADDITIONAL_PRODUCTS_POST,
  CAPS.V2_MERCHANTS_BANK_ACCOUNT_PUT,
  CAPS.V2_MERCHANTS_BUSINESS_DETAILS_PATCH,
  CAPS.V2_MERCHANTS_GET,
  CAPS.V2_MERCHANTS_PRODUCTS_PUT,
  CAPS.V2_MERCHANTS_STAKEHOLDERS_AND_OPERATIONAL_ADDRESS_PUT,
  CAPS.V2_MERCHANTS_STAKEHOLDERS_POST,
  CAPS.V2_MERCHANTS_STAKEHOLDERS_PUT,
  CAPS.V2_MERCHANTS_STORE_DETAILS_PUT,
  CAPS.V2_MERCHANTS_NEXT_STATUS_POST,
]);

const preAssessmentDataActions = capabilityUtils.getFinalizedActions([
  CAPS.V2_PRE_ASSESSMENT_PATCH,
  CAPS.V2_KYM_PRE_ASSESSMENT_POST,
]);

const recollectionActions = capabilityUtils.getFinalizedActions([
  CAPS.V2_MERCHANTS_RECOLLECTION_GET,
  CAPS.V2_MERCHANTS_RECOLLECTION_PUT,
  CAPS.V2_MERCHANTS_RECOLLECTION_POST,
]);

const phoneNumberActions = capabilityUtils.getFinalizedActions([
  CAPS.V2_MERCHANTS_PHONE_NUMBER_POST,
  CAPS.V2_MERCHANTS_PHONE_NUMBER_GET,
]);

const init = {
  data: { country: '' },
  documents: [],
  foundMerchant: null,
  needsMerchantRefresh: false,
  needsMerchantRefreshCount: 0,
  needsProductRefresh: false,
  needsProductRefreshCount: 0,
  isValidWidgetToken: null,
  preAssessment: {},
  underwritingStatus: null,
  contactPhoneNumber: null,
  recollection: { isInitialized: false, isSubmitted: false, stakeholders: [] },
  selectableMerchants: { eu: [], us: [], ap: [] },
};

export default handleActions(
  {
    [CAPS.V2_PRODUCTS_GET.LOADING]: state => ({
      ...state,
      needsProductRefresh: false,
    }),
    [CAPS.V2_MERCHANTS_GET.LOADING]: state => ({
      ...state,
      needsMerchantRefresh: false,
    }),
    [CAPS.V2_MERCHANTS_GET]: (state, { error, payload }) => {
      if (error) {
        return {
          ...state,
          foundMerchant: state.foundMerchant || false,
          needsMerchantRefresh: true, // refresh merchant because we had an error
          needsMerchantRefreshCount: state.needsMerchantRefreshCount + 1,
        };
      }

      return {
        ...state,
        foundMerchant: true,
        needsMerchantRefreshCount: 0,
        needsProductRefresh: payload.config.needsProductRefresh || false,
      };
    },
    [CAPS.V2_PRE_ASSESSMENT_UNDERWRITING_STATUS_GET]: (state, { error, payload }) => {
      const { data: status } = payload;
      return { ...state, underwritingStatus: error ? null : status };
    },
    [CAPS.V2_MERCHANTS_ADDITIONAL_PRODUCTS_POST]: state => ({
      ...state,
      needsProductRefresh: true,
    }),
    [combineActions(...preAssessmentDataActions)]: (state, { error, payload }) => {
      const { data: preAssessment } = payload;

      if (error) {
        return state;
      }

      preAssessment.showOverview = false;

      return { ...state, preAssessment };
    },
    [CAPS.V2_PRE_ASSESSMENT_GET]: (state, { error, payload }) => {
      const { data: preAssessment } = payload;

      if (error) {
        if (payload.response.data.errorCode === 'MOMA_SALESFORCE_INVALID_PREFILL_DATA') {
          return {
            ...state,
            error: payload.response.data,
          };
        }
        return state;
      }

      preAssessment.showOverview = preAssessment.isSubmitted;

      return { ...state, preAssessment };
    },
    [combineActions(...merchantUpdateDataActions)]: (state, { error, payload }) => {
      if (error) {
        return state;
      }

      const { stakeholders = [], ...rest } = payload.data || { country: '' };
      const data = {
        ...rest,
        stakeholders: stakeholders.map(({ role, ...s }) => s),
      };

      return {
        ...state,
        data,
      };
    },
    [CAPS.MERCHANTS_CREDENTIALS_POST]: (state, { error, payload }) => {
      if (error && !state.foundMerchant) {
        return {
          ...state,
          needsMerchantRefresh: true,
          needsMerchantRefreshCount: state.needsMerchantRefreshCount + 1,
        };
      }
      return {
        ...state,
        data: {
          ...state.data,
          credentials: [payload.data],
        },
      };
    },
    [CAPS.V2_MERCHANTS_FIRST_TRANSACTION_GET]: (state, { error, payload }) => {
      if (error) {
        return state;
      }
      const { firstOrderAt } = payload?.data;

      return {
        ...state,
        data: {
          ...state.data,
          firstOrderAt,
        },
        firstOrderAt,
      };
    },
    [CAPS.V2_WIDGET_TOKEN_GET]: (state, { error }) => {
      if (error) {
        return { ...state, isValidWidgetToken: false };
      }

      return { ...state, isValidWidgetToken: true };
    },
    [combineActions(...recollectionActions)]: (state, { error, payload }) => {
      if (error) {
        return { ...state, recollection: { ...state.recollection, isInitialized: true } };
      }

      return { ...state, recollection: { ...payload.data, isInitialized: true } };
    },
    [combineActions(...phoneNumberActions)]: (state, { error, payload }) => {
      if (error) {
        return state;
      }

      const {
        data: { phoneNumber: contactPhoneNumber },
      } = payload;

      return { ...state, contactPhoneNumber };
    },
    [CAPS.V2_MERCHANTS_GET_ALL]: (
      state,
      { error, payload: { config: { region = 'eu' } = {}, data } = {} },
    ) => {
      if (error) {
        return state;
      }

      const { selectableMerchants = init.selectableMerchants, ...rest } = state;

      return { ...rest, selectableMerchants: { ...selectableMerchants, [region]: data } };
    },
    [CAPS.V2_DOC_LIST]: (state, { payload }) => ({ ...state, documents: payload.data }),
  },
  init,
);
